import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import {
  AnswerBlock,
  TriCompareTable,
  SpecTable,
  Breadcrumb,
  Prose,
  ReviewStamp,
} from "@/components/site/ContentPrimitives";
import ArchitectureDiagram from "@/components/diagrams/ArchitectureDiagram";
import DecisionTree from "@/components/diagrams/DecisionTree";
import {
  useSEO,
  useJsonLd,
  SITE_URL,
  breadcrumbSchema,
  articleSchema,
} from "@/lib/seo";

export default function GpsVsBleVsCellular() {
  useSEO({
    title:
      "GPS vs BLE vs Cellular Package Tracking | Technology Guide",
    description:
      "How GNSS/GPS, Bluetooth Low Energy, and cellular technologies compare for disposable package tracking — accuracy, coverage, battery, cost, and best-fit lanes.",
    canonical: "/gps-vs-ble-vs-cellular",
    image: `${SITE_URL}/images/ll100-scan.jpg`,
  });

  useJsonLd(
    "breadcrumb-gbc",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "GPS vs BLE vs Cellular" },
    ]),
  );

  useJsonLd(
    "article-gbc",
    articleSchema({
      headline: "GPS vs BLE vs Cellular Package Tracking",
      description:
        "Objective technology comparison for disposable package tracking — accuracy, coverage, battery life, unit economics, best-fit lanes.",
      path: "/gps-vs-ble-vs-cellular",
      image: `${SITE_URL}/images/ll100-scan.jpg`,
      datePublished: "2026-02-01",
    }),
  );

  return (
    <div>
      <PageHero
        eyebrow="Technology guide"
        title="GPS vs BLE vs Cellular Package Tracking"
        subtitle="How the three primary location technologies used inside disposable package trackers compare — with honest guidance on when each is the right choice."
      />

      <section className="section">
        <div className="container-x">
          <Breadcrumb
            trail={[
              { label: "Home", href: "/" },
              { label: "Disposable Package Tracker", href: "/disposable-package-tracker" },
              { label: "GPS vs BLE vs Cellular" },
            ]}
          />

          <Prose>
            <h2 className="h2">The three technologies at a glance</h2>
            <AnswerBlock>
              GNSS/GPS gives precise open-sky position from satellites.
              Cellular provides the backhaul to move that data — and can
              also derive an approximate position from cell towers. BLE
              (Bluetooth Low Energy) resolves position through a network
              of nearby gateways. Most disposable devices combine two of
              these into a single architecture.
            </AnswerBlock>
            <TriCompareTable
              headers={["BLE smart label", "Cellular + GNSS", "Hybrid checkpoint (RFID/NFC)"]}
              rows={[
                {
                  q: "Position source",
                  values: [
                    "BLE gateway detections",
                    "GNSS satellites + cell + Wi-Fi assist",
                    "Reader events at fixed nodes",
                  ],
                },
                {
                  q: "Typical accuracy",
                  values: [
                    "Venue / node level (meters – tens of meters)",
                    "Open-sky GPS-grade (meters)",
                    "Discrete points along the lane",
                  ],
                },
                {
                  q: "Independent of external network?",
                  values: ["No — gateways required", "Yes — cellular + satellite", "No — readers required"],
                },
                {
                  q: "Typical battery life",
                  values: [
                    "Up to ~40 days",
                    "10 – 180+ days by reporting config",
                    "Passive — no battery",
                  ],
                },
                {
                  q: "Form factor / weight",
                  values: [
                    "Ultra-thin label (~4 g)",
                    "Thin device (~34 g)",
                    "Tag / sticker",
                  ],
                },
                {
                  q: "On-board sensors",
                  values: [
                    "Rare on ultra-thin class",
                    "Temp / humidity / shock / light",
                    "None on the tag",
                  ],
                },
                {
                  q: "Cost per shipment",
                  values: ["Low", "Higher (unit + connectivity)", "Very low"],
                },
                {
                  q: "Best-fit lanes",
                  values: [
                    "Partner-networked / venue-heavy lanes",
                    "Global, high-value, cold-chain, low-visibility legs",
                    "Closed distribution nodes",
                  ],
                },
              ]}
            />

            <h2 className="h2 mt-16">Architecture — how the data reaches the platform</h2>
            <ArchitectureDiagram />

            <h2 className="h2 mt-16">Verified specifications — reference devices</h2>
            <p className="body-text mt-3">
              The programme deploys two representative device classes. Their
              specifications are kept separate; blending specs across
              architectures leads to misleading claims. See the disposal and
              air-freight notes below each table.
            </p>

            <SpecTable
              title="Ultra-thin BLE disposable smart label"
              rows={[
                { k: "Wireless", v: "Bluetooth 5.3" },
                { k: "Dimensions", v: "65 × 55 mm" },
                { k: "Thickness", v: "0.4 – 1.2 mm" },
                { k: "Weight", v: "Approximately 4 g" },
                { k: "Battery", v: "Zinc-manganese, non-replaceable" },
                { k: "Battery life", v: "Up to 40 days (reporting-dependent)" },
                { k: "Range", v: "Up to 70 m BLE open-area" },
                { k: "Activation", v: "Peel / cut pull-tab" },
                { k: "Certifications", v: "CE, FCC, RoHS" },
              ]}
              note="Air-freight suitability is device-, chemistry-, airline- and lane-specific. Buyers should validate for their operating route."
            />

            <SpecTable
              title="4G cellular + GNSS disposable label"
              rows={[
                { k: "Cellular", v: "4G LTE / 2G fallback" },
                { k: "SIM", v: "Global eSIM" },
                { k: "GNSS", v: "GPS / GLONASS / BDS / QZSS" },
                { k: "Assisted position", v: "Wi-Fi + BLE" },
                { k: "Condition sensors", v: "Temperature, humidity, shock/vibration, ambient light" },
                { k: "Battery life", v: "10 – 180+ days depending on reporting configuration" },
                { k: "Dimensions", v: "101 × 177 × 4.7 mm" },
                { k: "Weight", v: "Approximately 34 g" },
              ]}
              note="Air-freight suitability is device-, chemistry-, airline- and lane-specific. Buyers should validate for their operating route."
            />

            <h2 className="h2 mt-16">When to pick which</h2>
            <DecisionTree />

            <h2 className="h2 mt-16">Common misconceptions</h2>
            <ul className="mt-4 space-y-3 body-text list-disc pl-6">
              <li>
                <strong className="text-ink">&quot;All disposable trackers are GPS trackers.&quot;</strong>{" "}
                No. Ultra-thin BLE labels do not include a GNSS receiver.
              </li>
              <li>
                <strong className="text-ink">&quot;BLE means indoor-only.&quot;</strong>{" "}
                No. BLE labels report anywhere a compatible gateway detects
                them — including partner facilities, driver phones, and
                fixed gateways along a lane.
              </li>
              <li>
                <strong className="text-ink">&quot;Cellular + GNSS is always better.&quot;</strong>{" "}
                No. It is bigger, heavier, and more expensive per unit; it
                is the right answer for lanes with genuine independence
                requirements or condition-monitoring needs.
              </li>
            </ul>

            <ReviewStamp
              date="February 2026"
              basis="Manufacturer specifications for the deployed BLE and cellular/GNSS label classes and direct deployment experience across UK/EU logistics lanes."
            />
          </Prose>
        </div>
      </section>

      <RelatedLinks
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels — BLE technology in detail" },
          { to: "/disposable-gps-tracker", label: "Disposable GPS Tracker — GNSS-class devices" },
          { to: "/how-disposable-trackers-work", label: "How Disposable Trackers Work — end-to-end lifecycle" },
        ]}
      />

      <CTAStrip
        title="Not sure which technology fits your lane?"
        body="A specialist will walk through the shipment profile and recommend the right architecture — BLE, cellular/GNSS, or hybrid."
      />
    </div>
  );
}
